import { useState } from "react";
import { useSelector } from "react-redux";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";

import { NoteType } from "./NoteType";
import UserAvatar from "../users/UserAvatar";
import { Note } from "./Note";
import { UserRoleEnum } from "../userRoles/UserRole";
import { PropsWithClassName } from "../../common/types/PropsWithClassName";
import NoteAttachmentDetails from "./NoteAttachmentDetails";
import { bulkDeleteAttachments, downloadAllAttachments } from "./notesApi";
import { selectAuthUser } from "../auth/authSlice";
import { Privilege } from "../auth/Privilege";
import { canAccess } from "../auth/utils";

interface NoteDetailsProps extends PropsWithClassName {
  note: Note;
  onAttachmentsDeleted?: () => void;
}

export default function NoteDetails({ className, note, onAttachmentsDeleted }: NoteDetailsProps) {
  const authUser = useSelector(selectAuthUser);
  const isAgentOrAdmin = canAccess(Privilege.Agent, authUser.privilege);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());
  const [isLoadingDownloadAttachments, setIsLoadingDownloadAttachments] = useState(false);
  const [isLoadingDeleteAttachments, setIsLoadingDeleteAttachments] = useState(false);
  const hasAttachments = note.noteAttachments.length > 0;

  const handleAttachmentsSelectionChange = (attachmentId: string, selected: boolean) => {
    setSelectedAttachmentIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(attachmentId);
      } else {
        next.delete(attachmentId);
      }
      return next;
    });
  };

  const handleBulkDeleteAttachments = () => {
    confirmDialog({
      header: "Confirmare stergere",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">
          Esti sigur ca vrei sa stergi <span className="font-bold">{selectedAttachmentIds.size} atasament(e)?</span>
        </div>
      ),
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: async () => {
        setIsLoadingDeleteAttachments(true);
        const ids = Array.from(selectedAttachmentIds);
        await bulkDeleteAttachments(note.id, ids);
        setSelectedAttachmentIds(new Set());
        setIsLoadingDeleteAttachments(false);
        onAttachmentsDeleted?.();
      },
    });
  };

  const handleDownloadAttachments = async () => {
    setIsLoadingDownloadAttachments(true);
    await downloadAllAttachments(note.id, Array.from(selectedAttachmentIds));
    setIsLoadingDownloadAttachments(false);
  };

  return (
    <div className={`${className} flex gap-2 p-3 border-round bg-white shadow-1"`}>
      <UserAvatar user={note.user} />
      <div className="flex flex-column gap-2">
        <div className="flex flex-column gap-1">
          <div className="flex align-items-center gap-2 font-semibold">
            <span>
              {note.user.firstName} {note.user.lastName}
            </span>
            {note.user.role === UserRoleEnum.Agent && <Tag>Agent</Tag>}
            {note.type === NoteType.Internal && (
              <div className="flex align-items-center gap-2 text-sm text-yellow-500">
                <i className="pi pi-eye-slash"></i>
                <div>Nota interna</div>
              </div>
            )}
          </div>
          <span className="text-sm text-500">{note.createdPretty}</span>
        </div>
        <div className="flex flex-column gap-4">
          <div className="raw-html-container" dangerouslySetInnerHTML={{ __html: note.noteText }}></div>
          {hasAttachments && (
            <div className="flex flex-column gap-4">
              <div className="flex flex-column gap-4">
                {note.noteAttachments.map((attachment) => (
                  <NoteAttachmentDetails
                    key={attachment.id}
                    attachment={attachment}
                    selectable={true}
                    selected={selectedAttachmentIds.has(attachment.id!)}
                    onSelectionChange={handleAttachmentsSelectionChange}
                  />
                ))}
              </div>
              <div className="flex align-items-center gap-2">
                {selectedAttachmentIds.size > 0 && (
                  <Button
                    text
                    size="small"
                    severity="secondary"
                    icon="pi pi-download"
                    label={`Descarca selectate (${selectedAttachmentIds.size})`}
                    loading={isLoadingDownloadAttachments}
                    onClick={handleDownloadAttachments}
                  />
                )}
                {isAgentOrAdmin && selectedAttachmentIds.size > 0 && (
                  <Button
                    text
                    size="small"
                    severity="danger"
                    icon="pi pi-trash"
                    label={`Sterge selectate (${selectedAttachmentIds.size})`}
                    loading={isLoadingDeleteAttachments}
                    onClick={handleBulkDeleteAttachments}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
